/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { z } from 'zod';

import { request } from '@/api/client';

/*
 * Signed documents, one at a time (`IMP-17` to `IMP-26`).
 *
 * Each document -- a PDF or a Word file (.docx or .doc) -- is its own
 * request, so one that fails is reported on its own row and the others carry
 * on (`IMP-22`). The server checks every limit again;
 * these are here so a wrong file is refused before it is sent.
 */

/** The most files one upload takes (`IMP-18`). */
export const MAX_DOCUMENTS = 50;

/** The largest document, in bytes: 25 MB (`IMP-18`). */
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;

export const PDF_TYPE = 'application/pdf';
export const DOCX_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
/** A Word 97-2003 document, stored as it came (`IMP-18`). */
export const DOC_TYPE = 'application/msword';

/**
 * The three kinds a signed document may be: its extension, the type it is
 * sent as, and the other names a browser gives the same kind.
 */
interface DocumentKind {
  extension: string;
  type: string;
  aliases: readonly string[];
}

const DOCUMENT_KINDS: readonly DocumentKind[] = [
  { extension: '.pdf', type: PDF_TYPE, aliases: [PDF_TYPE, 'application/x-pdf'] },
  { extension: '.docx', type: DOCX_TYPE, aliases: [DOCX_TYPE] },
  { extension: '.doc', type: DOC_TYPE, aliases: [DOC_TYPE, 'application/vnd.ms-word'] },
];

/** What the file picker offers: PDFs and Word documents, by name and by type. */
export const DOCUMENT_ACCEPT = [
  ...DOCUMENT_KINDS.map((kind) => kind.extension),
  ...DOCUMENT_KINDS.map((kind) => kind.type),
].join(',');

/**
 * The type a file is sent as, or `null` if it is not a PDF, a .docx or a .doc.
 *
 * By its name, in any case (`.PDF` as well as `.pdf`), or by the browser's
 * type where the name has no extension. A browser often has no type for a
 * PDF, or calls it `application/octet-stream`; the name decides then, and the
 * server reads the bytes anyway. Refused only when the name and the type name
 * two different kinds, since one of them is wrong.
 */
export function documentType(file: File): string | null {
  const name = file.name.trim().toLowerCase();
  const type = file.type.toLowerCase();
  const byName = DOCUMENT_KINDS.find((kind) => name.endsWith(kind.extension));
  const byType = DOCUMENT_KINDS.find((kind) => kind.aliases.includes(type));
  if (byName && byType && byName !== byType) return null;
  return (byName ?? byType)?.type ?? null;
}

/** Why a file of the wrong kind is not added. */
export const NOT_A_DOCUMENT = 'Only a PDF or Word (.docx or .doc) can be imported.';

export const importedDocumentSchema = z.object({
  agreement_id: z.string(),
  reference: z.string(),
  title: z.string(),
  store: z.string(),
});

export type ImportedDocument = z.infer<typeof importedDocumentSchema>;

/** What one document is, as the person described it. */
export interface DocumentDescription {
  workflowId: string;
  title: string;
  counterparty: string;
  /** `YYYY-MM-DD`. */
  signedOn: string;
}

/**
 * Why a file cannot be imported, before it is sent, or `null` if it can.
 *
 * By its type and its name both: a browser's idea of a type is sometimes
 * empty, and the server reads the first bytes anyway.
 */
export function documentRefusal(file: File): string | null {
  if (documentType(file) === null) return NOT_A_DOCUMENT;
  if (file.size === 0) return 'The file is empty.';
  if (file.size > MAX_DOCUMENT_BYTES) return 'A document may be at most 25 MB.';
  return null;
}

/** The earliest day a document may say it was signed, as the server holds it. */
export const EARLIEST_SIGNED = '1900-01-01';

/** Said under a signed date after today. */
export const SIGNED_IN_FUTURE = "The signed date can't be in the future.";

/** Said under a signed date before 1900. */
export const SIGNED_TOO_EARLY = "The signed date can't be before 1 January 1900.";

/**
 * What is wrong with the day a document was signed, or `null` if nothing is.
 *
 * `today` is Amida's today, as `YYYY-MM-DD` -- the day the server counts from,
 * so a person in another zone near midnight is held to the same day. Both are
 * ISO days, so they compare as text. An empty date is merely incomplete.
 */
export function signedOnProblem(signedOn: string, today: string): string | null {
  if (signedOn === '') return null;
  if (signedOn > today) return SIGNED_IN_FUTURE;
  if (signedOn < EARLIEST_SIGNED) return SIGNED_TOO_EARLY;
  return null;
}

/** The title a file suggests: its name, without the extension. */
export function titleFrom(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return (dot > 0 ? filename.slice(0, dot) : filename).trim();
}

export const importDocument = (file: File, description: DocumentDescription) => {
  const form = new FormData();
  // Sent as what the check above found, whatever the browser guessed; the
  // server checks the bytes.
  form.append(
    'file',
    new File([file], file.name, { type: documentType(file) ?? PDF_TYPE }),
  );
  form.append('workflow_id', description.workflowId);
  form.append('title', description.title.trim());
  form.append('counterparty', description.counterparty.trim());
  form.append('signed_on', description.signedOn);
  return request('/api/v1/imports/documents', importedDocumentSchema, {
    method: 'POST',
    body: form,
  });
};
